import React, { useState, useEffect, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  incrementViewCount, 
  likePost, 
  unlikePost, 
  getPostById 
} from '../../data/postsData';
import { MOCK_HOME_DATA, MOCK_POST_DETAIL_DATA } from '../../api/mockData';
import './index.css';

// 配置抽离（保持和 PostDetail 风格一致）
const CONFIG = {
  DEFAULT_READ_TIME: 5,
  DEFAULT_DATE: '2026-01-08',
  DEFAULT_CATEGORY_ICON: '📄',
  DEFAULT_CATEGORY_COLOR: 'linear-gradient(45deg, #FFB8D9, #FF6B9D)',
  INTERACTION_ICONS: {
    like: { active: '❤️', inactive: '🤍' },
    comment: '💬',
    view: '👁️',
    readMore: { icon: '🐶', arrow: '→' }
  },
  DECORATION_ICONS: {
    paw: '🐾',
    bone: '🦴',
    ball: '⚽',
    title: '🐕‍🦺',
    date: '📅',
    time: '⏱️',
    tag: '🏷️',
    featured: '⭐',
    avatar: '👤',
    draft: '📝' // 新增：草稿标签图标
  },
  EMPTY_TEXT: {
    title: '无标题',
    excerpt: '暂无摘要',
    category: '未分类',
    date: '未知日期',
    authorName: '未知作者',
    authorRole: ''
  },
  // 新增：草稿相关配置
  DRAFT_CONFIG: {
    labelText: '草稿',
    labelColor: 'linear-gradient(45deg, #8FD3FF, #3AA8FF)',
    hideInteractions: true, // 草稿隐藏点赞/评论/阅读量
    readMoreText: '编辑草稿' // 草稿的“阅读全文”改为“编辑草稿”
  }
};

const PostCard = memo(({ post }) => {
  const navigate = useNavigate();
  
  // 核心状态（和 PostDetail 对齐）
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(post?.likes || 0);
  const [viewCount, setViewCount] = useState(post?.views || 0);
  const [commentCount, setCommentCount] = useState(post?.comments || 0);

  // 新增：判断是否为草稿（优先用 status，兼容 published 字段）
  const isDraft = post?.status === 'draft' || (post?.published === false);

  // 初始化：和 PostDetail 完全一致的本地存储读取逻辑
  useEffect(() => {
    // 仅对已发布文章执行阅读量/点赞状态初始化
    if (post?.id && !isDraft) {
      // 1. 增加阅读量
      const newViewCount = incrementViewCount(post.id);
      setViewCount(newViewCount);

      // 2. 读取本地存储的点赞状态（和 PostDetail 用同一个 KEY）
      const likedPostsStr = localStorage.getItem(MOCK_POST_DETAIL_DATA.CONFIG.LOCAL_STORAGE_KEYS.likedPosts) || '[]';
      const likedPosts = JSON.parse(likedPostsStr);
      setIsLiked(likedPosts.includes(post.id));

      // 3. 初始化点赞数/评论数
      setLikeCount(post.likes || 0);
      setCommentCount(post.comments || 0);
    } else if (isDraft) {
      // 草稿重置互动数据
      setLikeCount(0);
      setViewCount(0);
      setCommentCount(0);
      setIsLiked(false);
    }

    // 监听全局数据变化（和 PostDetail 一致）
    const handleDataChange = (event) => {
      if (event.detail?.postId === post?.id && !isDraft) {
        const updatedPost = getPostById(post.id);
        if (updatedPost) {
          setLikeCount(updatedPost.likes || 0);
          setCommentCount(updatedPost.comments || 0);
          setViewCount(updatedPost.views || 0);
          
          // 同步点赞状态
          const likedPostsStr = localStorage.getItem(MOCK_POST_DETAIL_DATA.CONFIG.LOCAL_STORAGE_KEYS.likedPosts) || '[]';
          const likedPosts = JSON.parse(likedPostsStr);
          setIsLiked(likedPosts.includes(post.id));
        }
      }
    };

    window.addEventListener('blogDataChanged', handleDataChange);
    return () => window.removeEventListener('blogDataChanged', handleDataChange);
  }, [post?.id, post?.likes, post?.comments, isDraft]);

  // 日期格式化（保留原有逻辑）
  const formatDate = (dateString) => {
    if (!dateString) return CONFIG.EMPTY_TEXT.date;
    try {
      return new Date(dateString).toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return CONFIG.EMPTY_TEXT.date;
    }
  };

  // 分类图标（保留原有逻辑）
  const getCategoryIcon = (category) => {
    return MOCK_HOME_DATA.CATEGORY_ICONS[category] || CONFIG.DEFAULT_CATEGORY_ICON;
  };

  // 新增：处理草稿的“编辑草稿”/已发布的“阅读全文”
  const handleReadMore = () => {
    if (post?.id) {
      // 草稿跳转到编辑页，已发布跳转到详情页
      const path = isDraft ? `/write/${post.id}` : `/post/${post.id}`;
      navigate(path);
    }
  };

  // 👇 核心修复：完全照搬 PostDetail 的 handleLike 逻辑 👇
  const handleLike = (e) => {
    // 草稿不允许点赞
    if (isDraft) return;
    
    // 仅保留 PostCard 特有的阻止冒泡（避免触发跳转）
    e.stopPropagation();
    if (!post) return;
    
    // 1. 读取本地存储（和 PostDetail 完全一致）
    const likedPosts = JSON.parse(localStorage.getItem(MOCK_POST_DETAIL_DATA.CONFIG.LOCAL_STORAGE_KEYS.likedPosts) || '[]');
    const alreadyLiked = likedPosts.includes(post.id);
    
    if (alreadyLiked) {
      // 取消点赞（和 PostDetail 逻辑一致）
      const newLikeCount = unlikePost(post.id); // PostDetail 是 parseInt(id)，这里直接用 post.id
      setLikeCount(newLikeCount); // 即时更新点赞数
      
      const newLikedPosts = likedPosts.filter(postId => postId !== post.id);
      localStorage.setItem(MOCK_POST_DETAIL_DATA.CONFIG.LOCAL_STORAGE_KEYS.likedPosts, JSON.stringify(newLikedPosts));
      setIsLiked(false);
    } else {
      // 点赞（和 PostDetail 逻辑一致）
      const newLikeCount = likePost(post.id);
      setLikeCount(newLikeCount); // 即时更新点赞数
      
      likedPosts.push(post.id);
      localStorage.setItem(MOCK_POST_DETAIL_DATA.CONFIG.LOCAL_STORAGE_KEYS.likedPosts, JSON.stringify(likedPosts));
      setIsLiked(true);
    }
  };

  // 加载状态（保留原有逻辑）
  if (!post) {
    return (
      <div className="post-card fade-in loading">
        <div className="post-skeleton">
          <div className="skeleton-header"></div>
          <div className="skeleton-title"></div>
          <div className="skeleton-excerpt"></div>
          <div className="skeleton-footer"></div>
        </div>
      </div>
    );
  }

  const author = post.author || {};

  // 渲染部分（保留原有样式，新增草稿适配）
  return (
    <div className={`post-card fade-in ${isDraft ? 'draft-card' : ''}`}>
      {/* 新增：草稿标签 */}
      {isDraft && (
        <div 
          className="draft-badge"
          style={{ background: CONFIG.DRAFT_CONFIG.labelColor }}
        >
          <span className="badge-icon">{CONFIG.DECORATION_ICONS.draft}</span>
          {CONFIG.DRAFT_CONFIG.labelText}
        </div>
      )}

      <div className="post-decoration">
        <span className="decoration-paw">{CONFIG.DECORATION_ICONS.paw}</span>
        <span className="decoration-bone">{CONFIG.DECORATION_ICONS.bone}</span>
        <span className="decoration-ball">{CONFIG.DECORATION_ICONS.ball}</span>
      </div>

      <div className="post-header">
        <div className="post-meta-left">
          <span 
            className="post-category"
            style={{ background: post.categoryColor || CONFIG.DEFAULT_CATEGORY_COLOR }}
          >
            <span className="category-icon">{getCategoryIcon(post.category)}</span>
            {post.category || CONFIG.EMPTY_TEXT.category}
          </span>
          <span className="post-date">
            <span className="date-icon">{CONFIG.DECORATION_ICONS.date}</span>
            {formatDate(post.date || post.createdAt || CONFIG.DEFAULT_DATE)}
          </span>
        </div>
        <div className="post-meta-right">
          <span className="post-read-time">
            <span className="time-icon">{CONFIG.DECORATION_ICONS.time}</span>
            {post.readTime || CONFIG.DEFAULT_READ_TIME}分钟阅读
          </span>
        </div>
      </div>

      <h3 className="post-title">
        <span className="title-decoration">{CONFIG.DECORATION_ICONS.title}</span>
        {post.title || CONFIG.EMPTY_TEXT.title}
      </h3>

      <div className="post-excerpt-container">
        <p className="post-excerpt">{post.excerpt || CONFIG.EMPTY_TEXT.excerpt}</p>
        {post.featured && !isDraft && ( // 草稿不显示精选标签
          <span className="featured-badge">
            <span className="badge-icon">{CONFIG.DECORATION_ICONS.featured}</span>
            精选文章
          </span>
        )}
      </div>

      {post.tags && post.tags.length > 0 && (
        <div className="post-tags">
          {post.tags.map((tag) => (
            <span key={`${post.id}-${tag}`} className="post-tag">
              <span className="tag-icon">{CONFIG.DECORATION_ICONS.tag}</span>
              {tag}
            </span>
          ))}
        </div>
      )}

      <div className="post-footer">
        <div className="post-author">
          <div className="author-avatar">
            <span className="avatar-icon">
              {author.avatar || CONFIG.DECORATION_ICONS.avatar}
            </span>
          </div>
          <div className="author-info">
            <span className="author-name">
              {author.name || CONFIG.EMPTY_TEXT.authorName}
            </span>
            <span className="author-role">
              {author.role || CONFIG.EMPTY_TEXT.authorRole}
            </span>
          </div>
        </div>

        <div className="post-interactions">
          {/* 草稿隐藏互动按钮 */}
          {!CONFIG.DRAFT_CONFIG.hideInteractions || !isDraft ? (
            <>
              <button 
                className={`interaction-btn like-btn ${isLiked ? 'liked' : ''}`}
                onClick={handleLike}
                aria-label="点赞"
                disabled={isDraft} // 草稿禁用点赞
              >
                <span className="btn-icon">{isLiked ? CONFIG.INTERACTION_ICONS.like.active : CONFIG.INTERACTION_ICONS.like.inactive}</span>
                <span className="btn-count">{likeCount}</span>
              </button>
              
              <button className="interaction-btn comment-btn" disabled={isDraft}>
                <span className="btn-icon">{CONFIG.INTERACTION_ICONS.comment}</span>
                <span className="btn-count">{commentCount}</span>
              </button>
              
              <button className="interaction-btn view-btn" disabled={isDraft}>
                <span className="btn-icon">{CONFIG.INTERACTION_ICONS.view}</span>
                <span className="btn-count">{viewCount}</span>
              </button>
            </>
          ) : null}
          
          <button 
            className="btn btn-primary read-more-btn"
            onClick={handleReadMore}
          >
            <span className="btn-icon">{CONFIG.INTERACTION_ICONS.readMore.icon}</span>
            {isDraft ? CONFIG.DRAFT_CONFIG.readMoreText : '阅读全文'}
            <span className="btn-arrow">{CONFIG.INTERACTION_ICONS.readMore.arrow}</span>
          </button>
        </div>
      </div>
    </div>
  );
});

export default PostCard;